'use client'

import ChatDisplay, { ChatDisplayRef } from '@/components/ChatV2/ChatDisplay'
import ChatErrorDisplay from '@/components/ChatV2/ChatErrorDisplay'
import ChatHeader from '@/components/ChatV2/ChatHeader'
import ChatInput from '@/components/ChatV2/ChatInput'
import { analytics } from '@/lib/analytics'
import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { setError } from '@/lib/features/copilotv2/ChatSlice'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { setInitialMessage } from '@/lib/features/copilotv2/copilotInitialMessageStorage'
import { getEventStreamService } from '@/lib/features/events/EventStreamService'
import {
  useGetSessionQuery,
  useSetSessionBookmarkMutation,
  useUpdateSessionMutation,
} from '@/lib/features/session/sessionApi'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { useAppDispatch } from '@/lib/hooks'
import { SendMessageOptions } from '@/lib/type'
import { cn } from '@/lib/utils'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { CopilotSessionResponse } from 'sprites-types/api/v2/copilot'
import type { SessionsListChangedEvent } from 'sprites-types/api/v2/event-stream'

type ChatProps = {
  className?: string
}

export default function Chat({ className = '' }: ChatProps) {
  const { sendMessage, status, sessionId, deleteSession, hasHydrated, currentAgent } =
    useCopilotContext()

  const { data: userData } = useGetUserQuery()
  const workspaceId = userData?.workspace?.id
  const currentBusinessProfileId = useCurrentBusinessProfileId()
  const router = useRouter()
  const isDebug = useSearchParams().get('debug') === 'true'
  const dispatch = useAppDispatch()

  // Get session data from RTK Query instead of context
  const { data: sessionData } = useGetSessionQuery(sessionId || '', {
    skip: !sessionId,
  })
  const [updateSession] = useUpdateSessionMutation()
  const [setSessionBookmark] = useSetSessionBookmarkMutation()

  const threadSummary = sessionData?.threadSummary
  const isBookmarked = sessionData?.isBookmarked

  const updateThreadSummary = async (summary: string) => {
    if (!sessionId) return
    try {
      await updateSession({ sessionId, name: summary }).unwrap()
    } catch (err) {
      console.error('[Chat] Failed to update thread summary:', err)
    }
  }

  const toggleBookmark = async () => {
    if (!sessionId) return
    try {
      await setSessionBookmark({
        sessionId,
        bookmarked: !isBookmarked,
      }).unwrap()
    } catch (err) {
      console.error('[Chat] Failed to toggle bookmark:', err)
      toast.error("Couldn't toggle bookmark")
    }
  }

  const chatDisplayRef = useRef<ChatDisplayRef>(null)
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const [scrollContainerKey, setScrollContainerKey] = useState(0)

  // Get scroll container from ChatDisplay and trigger re-render when available
  useEffect(() => {
    const checkContainer = () => {
      if (chatDisplayRef.current) {
        const container = chatDisplayRef.current.getScrollContainer()
        if (container && container !== scrollContainerRef.current) {
          scrollContainerRef.current = container
          setScrollContainerKey((prev) => prev + 1)
        }
      }
    }

    // Check immediately
    checkContainer()

    // Retry after a short delay to handle async mounting
    const timeoutId = setTimeout(checkContainer, 50)

    return () => clearTimeout(timeoutId)
  }, [sessionId, hasHydrated])

  // Redirect away if this thread is deleted from another tab
  useEffect(() => {
    if (!sessionId) return

    const eventService = getEventStreamService()
    const handleSessionDeleted = (event: SessionsListChangedEvent) => {
      if (event.changeType === 'deleted' && event.sessionId === sessionId) {
        toast.info('This thread was deleted')
        router.push('/copilot')
      }
    }

    eventService.on('sessions_list_changed', handleSessionDeleted)
    return () => eventService.off('sessions_list_changed', handleSessionDeleted)
  }, [sessionId, router])

  // ChatInput will manage its own placeholder via its props; we now base the thinking animation
  // solely on SSE connection state inside ChatDisplay, so do not compute an enhanced flag here.
  const enhancedShowThinkingAnimation = false

  // Show blank state while hydrating an existing session
  if (sessionId && !hasHydrated) {
    return (
      <div
        className={cn(
          'relative flex h-full flex-col items-center',
          'w-full min-w-[320px]',
          className
        )}
        role="main"
        aria-label="Loading conversation"
      >
        <div className="flex h-full w-full flex-col" />
      </div>
    )
  }

  const handleSendMessage = async (message: string, options: SendMessageOptions) => {
    // 1. User submits a message
    console.log('[Chat] User submitted message', {
      message: message?.substring(0, 100) + (message?.length > 100 ? '...' : ''),
      options,
      fileCount: options?.files?.length ?? 0,
      fileNames: options?.files?.map((f) => f.name) || [],
      timestamp: Date.now(),
    })

    // Scroll to show new message after DOM updates
    requestAnimationFrame(() => {
      if (chatDisplayRef.current) {
        chatDisplayRef.current.scrollToOptimalPosition()
      }
    })

    // If no sessionId, create a new session and navigate
    // Note: setSending is already called in ChatInput before this function
    if (!sessionId && workspaceId) {
      try {
        // 2. Request sent (new session)
        console.log('[Chat] Sending request to create new session', {
          workspaceId,
          message,
          options,
          fileCount: options?.files?.length ?? 0,
          timestamp: Date.now(),
        })
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ workspaceId, businessProfileId: currentBusinessProfileId }),
          }
        )
        // 3. Response received (new session)
        console.log('[Chat] Received response for session creation', {
          ok: res.ok,
          status: res.status,
          timestamp: Date.now(),
        })
        if (!res.ok) throw new Error('Failed to create session')
        const data = (await res.json()) as CopilotSessionResponse
        const newSessionId = data.session.id

        // Track chat creation event
        analytics.copilot.userCreatedChat(newSessionId, 'chat_input')

        // If files are present, use already-uploaded URLs or upload if needed
        let fileLinks: { url: string; filename: string; ext: string; metadata?: any }[] | undefined
        if (options?.files && options.files.length > 0) {
          const uploadPromises = options.files.map(async (file) => {
            const fileWithMeta = file as any
            // If file was already uploaded, use existing URL
            if (fileWithMeta.uploadedUrl) {
              return {
                url: fileWithMeta.uploadedUrl,
                filename: file.name,
                ext: file.name.split('.').pop() || '',
                ...(fileWithMeta.imageMetadata ? { metadata: fileWithMeta.imageMetadata } : {}),
              }
            }
            // Otherwise upload now (fallback for any files that weren't pre-uploaded)
            const formData = new FormData()
            formData.append('file', file)
            const uploadRes = await fetch(
              `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/upload-file`,
              { method: 'POST', body: formData, credentials: 'include' }
            )
            if (!uploadRes.ok) {
              throw new Error(`File upload failed: ${uploadRes.status} ${uploadRes.statusText}`)
            }
            const body = await uploadRes.json()
            if (!body.url) throw new Error('No file URL returned from upload')
            return {
              url: body.url,
              filename: file.name,
              ext: file.name.split('.').pop() || '',
              ...(fileWithMeta.imageMetadata ? { metadata: fileWithMeta.imageMetadata } : {}),
            }
          })
          fileLinks = await Promise.all(uploadPromises)
        }

        console.log('[Chat] Storing initial message (links persisted):', {
          sessionId: newSessionId,
          message,
          hasLinks: !!fileLinks,
          linkCount: fileLinks?.length || 0,
          agentType: currentAgent,
        })
        await setInitialMessage(newSessionId, {
          message,
          options,
          fileLinks,
          agentType: currentAgent,
        })
        // Navigate to new session page
        router.replace(`/copilot/${newSessionId}${isDebug ? '?debug=true' : ''}`)
        return
      } catch (err: any) {
        console.error('Failed to create session:', err)
        // Set error state so it's displayed to the user
        const errorMessage = `Failed to create new conversation: ${err?.message || 'Network error'}`
        dispatch(setError(errorMessage))
        return
      }
    }

    // 2. Request sent (existing session)
    console.log('[Chat] Sending message to copilot', {
      message,
      options,
      fileCount: options?.files?.length ?? 0,
      fileNames: options?.files?.map((f) => f.name) || [],
      sessionId,
      timestamp: Date.now(),
    })

    try {
      // If sessionId exists, send as normal
      await sendMessage(message, options)
      // 3. Response received (existing session)
      console.log('[Chat] Message sent, awaiting response', {
        message,
        options,
        hasFiles: (options?.files?.length ?? 0) > 0,
        sessionId,
        timestamp: Date.now(),
      })
    } catch (err: any) {
      console.error('Failed to send message:', err)
      // Error is already handled by sendMessage in CopilotContext
      // The error state will be set and ChatErrorDisplay will show
    }
  }

  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-center',
        'w-full min-w-[320px]',
        className
      )}
      role="main"
      aria-label="Streaming chat interface"
    >
      {sessionId && (
        <ChatHeader
          key={scrollContainerKey}
          threadName={threadSummary || 'New Chat'}
          isBookmarked={isBookmarked}
          onRename={updateThreadSummary}
          onToggleBookmark={toggleBookmark}
          onDelete={deleteSession}
          scrollContainerRef={scrollContainerRef}
        />
      )}
      <div className="flex h-full w-full flex-col">
        <ChatDisplay ref={chatDisplayRef} className="flex-1 overflow-y-hidden" />

        {/* Error display */}
        <div className="w-full">
          <ChatErrorDisplay />
        </div>

        {/* Input area */}
        <section className="px-4 pb-4 pt-2">
          <ChatInput
            onSendMessageAction={handleSendMessage}
            status={status}
            showThinkingAnimation={enhancedShowThinkingAnimation}
          />
        </section>
      </div>
    </div>
  )
}
